import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid } from 'semantic-ui-react';
import { TenantDto } from 'ts-types/api.types';
import { addTenant, editTenant, getDatabaseNames } from 'services/tenantServices';
import Input from 'components/final-form/Input';
import { required } from 'utils/validatorUtils';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import Select from 'components/final-form/Select';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  tenantId?: string,
  tenant?: TenantDto
}

interface State {
  errorMessage?: string,
  databases: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class UpsertTenantForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    getDatabaseNames().then(results => {
      let databases: Array<DropdownOption> = results.map(db => ({
        key: db,
        text: db,
        value: db
      }));

      if (props.tenant && !databases.map(x => x.key).includes(props.tenant.dbName)) {
        databases.push({
          key: props.tenant.dbName,
          text: props.tenant.dbName,
          value: props.tenant.dbName
        })
      }
      this.setState({databases: databases});
    });

    this.state = {
      databases: [],
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<TenantDto>) => {

    let saveValues: Partial<TenantDto> = {...values};

    const {tenantId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general"),
        cancelTokenSource
      })
    };

    if (tenantId) {
      await editTenant(tenantId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addTenant(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  render(): React.ReactNode {

    const defaultValues: Partial<TenantDto> = {};

    const {tenant = defaultValues} = this.props;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={tenant}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {tenantId, getMessage, onCancel} = this.props;
    const {databases} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("tenant.id")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="id"
                        component={Input}
                        validate={required}
                        disabled={!!tenantId}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("tenant.dbName")}
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <Field
                        fluid
                        name="dbName"
                        component={Select}
                        options={databases}
                        allowAdditions={true}
                        search={true}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertTenantForm);
